import type {
  DataFileListItem,
  DriverListItem,
  KartListItem,
  KartSetupHistoryEntry,
  KartSetupSummary,
  UpdateTelemetrySessionContextRequest,
} from '@kangaroo/shared';

import type { MyChronSessionExport } from './telemetrySession';
import { sessionRecordedAtMs } from './telemetrySession';
import { formatRecordedAtMs } from './formatSessionDate';
import { resolveTimezoneFromMapPreview } from './sessionTimezone';

export const DEMO_TELEMETRY_SESSION = {
  key: 'demo:a_0180',
  title: 'Demo session',
  loadUrl: '/demo/a_0180.xrz',
} as const;

export type TelemetrySessionOption = {
  key: string;
  title: string;
  subtitle: string;
  loadUrl: string;
  dataFile?: DataFileListItem;
};

function normalizeName(value: string) {
  return value.trim().toLowerCase();
}

function normalizeNumber(value: string) {
  return value.replace(/^#/, '').trim();
}

export function findDriverByName(drivers: DriverListItem[], name: string | null | undefined) {
  if (!name) {
    return null;
  }

  const normalized = normalizeName(name);
  return (
    drivers.find((driver) => normalizeName(driver.name) === normalized) ??
    drivers.find(
      (driver) =>
        normalizeName(driver.name).includes(normalized) || normalized.includes(normalizeName(driver.name)),
    ) ??
    null
  );
}

export function findKartByNumber(karts: KartListItem[], number: string | null | undefined) {
  if (!number) {
    return null;
  }

  const normalized = normalizeNumber(number);
  return karts.find((kart) => normalizeNumber(kart.kartNumber) === normalized) ?? null;
}

export function findDriverDefaultKart(karts: KartListItem[], driverId: string) {
  return karts.find((kart) => kart.assignedDriverId === driverId) ?? null;
}

function resolveDataFileSessionTimezone(file: DataFileListItem) {
  return resolveTimezoneFromMapPreview(file.mapPreview);
}

export function resolveDataFileRecordedAtMs(file: DataFileListItem, recordedAtMsOverride?: number | null) {
  if (recordedAtMsOverride != null && Number.isFinite(recordedAtMsOverride)) {
    return recordedAtMsOverride;
  }

  if (file.summary.recordedAtMs != null && Number.isFinite(file.summary.recordedAtMs)) {
    return file.summary.recordedAtMs;
  }

  if (file.summary.sessionStartedAt) {
    const parsed = Date.parse(file.summary.sessionStartedAt);
    if (Number.isFinite(parsed)) {
      return parsed;
    }
  }

  const uploaded = Date.parse(file.uploadedAt);
  return Number.isFinite(uploaded) ? uploaded : null;
}

/** Same recorded-at precedence as session list + weather: context, export metadata, then upload summary. */
export function resolveSessionRecordedAtAnchorMs(
  session: MyChronSessionExport,
  options?: {
    contextRecordedAtMs?: number | null;
    dataFileRecordedAtMs?: number | null;
    timeZone?: string | null;
  },
): number | null {
  if (options?.contextRecordedAtMs != null && Number.isFinite(options.contextRecordedAtMs)) {
    return options.contextRecordedAtMs;
  }

  const fromExport = sessionRecordedAtMs(session, options?.timeZone);
  if (fromExport != null) {
    return fromExport;
  }

  if (options?.dataFileRecordedAtMs != null && Number.isFinite(options.dataFileRecordedAtMs)) {
    return options.dataFileRecordedAtMs;
  }

  return null;
}

export function buildDataFileSessionTitle(file: DataFileListItem, recordedAtMsOverride?: number | null) {
  const timeZone = resolveDataFileSessionTimezone(file);
  const recordedAtMs = resolveDataFileRecordedAtMs(file, recordedAtMsOverride);
  if (recordedAtMs === null) {
    return 'Recent session';
  }

  return formatRecordedAtMs(recordedAtMs, timeZone, 'date') ?? 'Recent session';
}

export function resolveDataFileSessionDriverName(
  file: DataFileListItem,
  driverNameOverride?: string | null,
) {
  if (file.sessionAssignment) {
    return file.sessionAssignment.driverName?.trim() || null;
  }

  return driverNameOverride?.trim() || file.summary.driverName?.trim() || null;
}

export function applyDataFileSessionAssignment(
  file: DataFileListItem,
  sessionKey: string,
  driverId: string | null,
  driverName: string | null,
) {
  return file.id === sessionKey
    ? {
        ...file,
        sessionAssignment: { driverId, driverName },
      }
    : file;
}

export function buildDataFileSessionSubtitle(
  file: DataFileListItem,
  recordedAtMsOverride?: number | null,
  driverNameOverride?: string | null,
) {
  const timeZone = resolveDataFileSessionTimezone(file);
  const recordedAtMs = resolveDataFileRecordedAtMs(file, recordedAtMsOverride);
  const parts: string[] = [];
  if (recordedAtMs !== null) {
    const sessionTime = formatRecordedAtMs(recordedAtMs, timeZone, 'time');
    if (sessionTime) {
      parts.push(sessionTime);
    }
  }
  const driverName = resolveDataFileSessionDriverName(file, driverNameOverride);
  parts.push(driverName ? `Driver ${driverName}` : 'Driver unassigned');
  if (file.summary.kartNumber) {
    parts.push(`#${normalizeNumber(file.summary.kartNumber)}`);
  }
  if (file.summary.trackName) {
    parts.push(file.summary.trackName);
  }
  if (file.summary.lapCount) {
    parts.push(`${file.summary.lapCount} laps`);
  }

  return parts.length > 0
    ? parts.join(' · ')
    : file.originalName.replace(/\.json$/i, '');
}

export function buildTelemetrySessionOptions(
  dataFiles: DataFileListItem[],
  demoGpsSamples?: number | null,
  recordedAtOverrides?: Record<string, number | null | undefined>,
  uploaderNamesByUserId?: Record<string, string>,
  driverNameOverridesByDataFileId?: Record<string, string | null | undefined>,
): TelemetrySessionOption[] {
  const options: TelemetrySessionOption[] = [];

  if (dataFiles.length === 0) {
    options.push({
      key: DEMO_TELEMETRY_SESSION.key,
      title: 'Demo session',
      subtitle: `MyChron export · ${demoGpsSamples?.toLocaleString() ?? '—'} GPS fixes`,
      loadUrl: DEMO_TELEMETRY_SESSION.loadUrl,
    });
  }

  const uploadedSessions = [...dataFiles]
    .sort((left, right) => new Date(right.uploadedAt).getTime() - new Date(left.uploadedAt).getTime())
    .map((file) => ({
      key: file.id,
      title: buildDataFileSessionTitle(file, recordedAtOverrides?.[file.id]),
      subtitle: (() => {
        const resolvedDriverName = driverNameOverridesByDataFileId?.[file.id];
        const base = buildDataFileSessionSubtitle(file, recordedAtOverrides?.[file.id], resolvedDriverName);
        const uploaderName = uploaderNamesByUserId?.[file.uploadedByUserId]?.trim() ?? null;
        if (!uploaderName) {
          return base;
        }

        const driverName = resolveDataFileSessionDriverName(file, resolvedDriverName);
        const samePerson = driverName ? normalizeName(driverName) === normalizeName(uploaderName) : false;
        return samePerson ? base : `${base} · Uploaded by ${uploaderName}`;
      })(),
      loadUrl: file.downloadUrl,
      dataFile: file,
    }));

  return [...options, ...uploadedSessions];
}

export function pickInitialTelemetrySession(dataFiles: DataFileListItem[]): TelemetrySessionOption | null {
  if (dataFiles.length > 0) {
    const file = [...dataFiles].sort(
      (left, right) => new Date(right.uploadedAt).getTime() - new Date(left.uploadedAt).getTime(),
    )[0]!;

    return {
      key: file.id,
      title: buildDataFileSessionTitle(file),
      subtitle: buildDataFileSessionSubtitle(file),
      loadUrl: file.downloadUrl,
      dataFile: file,
    };
  }

  if (dataFiles.length === 0) {
    return {
      key: DEMO_TELEMETRY_SESSION.key,
      title: 'Demo session',
      subtitle: 'MyChron export',
      loadUrl: DEMO_TELEMETRY_SESSION.loadUrl,
    };
  }

  return null;
}

export function resolveInitialTelemetrySession(
  options: TelemetrySessionOption[],
  dataFiles: DataFileListItem[],
  requestedSessionKey: string | null,
) {
  if (requestedSessionKey) {
    const requested = options.find((option) => option.key === requestedSessionKey) ?? null;
    return requested
      ? { option: requested, error: null }
      : {
          option: null,
          error: 'The requested telemetry session is unavailable or you do not have access.',
        };
  }

  return { option: pickInitialTelemetrySession(dataFiles), error: null };
}

export function buildSessionAssociationUpdates(
  session: MyChronSessionExport,
  drivers: DriverListItem[],
  karts: KartListItem[],
  current: {
    driverId: string | null;
    driverAssignmentSet?: boolean;
    kartId: string | null;
  },
  dataFile?: DataFileListItem | null,
): UpdateTelemetrySessionContextRequest | null {
  const updates: UpdateTelemetrySessionContextRequest = {};
  const savedDriverValid = Boolean(current.driverId && drivers.some((driver) => driver.id === current.driverId));
  const savedKartValid = Boolean(current.kartId && karts.some((kart) => kart.id === current.kartId));

  if (!savedDriverValid && current.driverAssignmentSet !== true) {
    const driverName = dataFile?.summary.driverName ?? session.metadata?.driver ?? null;
    const driver =
      findDriverByName(drivers, driverName) ??
      (dataFile?.uploadedByUserId
        ? drivers.find((entry) => entry.userId === dataFile.uploadedByUserId) ?? null
        : null);

    if (driver) {
      updates.driverId = driver.id;
    }
  }

  if (!savedKartValid) {
    const kartNumber = dataFile?.summary.kartNumber ?? session.metadata?.vehicle ?? null;
    const driverId = savedDriverValid ? current.driverId : updates.driverId ?? null;

    let kart = findKartByNumber(karts, kartNumber);
    if (!kart && driverId) {
      kart = findDriverDefaultKart(karts, driverId);
    }

    updates.kartId = kart?.id ?? null;
  }

  if (dataFile?.id) {
    updates.dataFileId = dataFile.id;
  }

  return Object.keys(updates).length > 0 ? updates : null;
}

export function formatKartSubtitle(kart: KartListItem) {
  const parts = [kart.kartClass, kart.nickname, kart.chassisModel].filter(Boolean);
  return parts.length > 0 ? parts.join(' · ') : 'Team kart';
}

export function resolveSetupHistory(kart: KartListItem): KartSetupHistoryEntry[] {
  if (kart.setupHistory.length > 0) {
    return kart.setupHistory;
  }

  return [
    {
      id: 'current',
      setup: kart.setup,
      notes: kart.notes,
      createdAt: kart.updatedAt,
    },
  ];
}

export function findSetupAtTime(
  kart: KartListItem,
  recordedAtMs: number | null | undefined,
): KartSetupHistoryEntry {
  const history = resolveSetupHistory(kart);

  if (recordedAtMs == null) {
    return history[0]!;
  }

  const atTime =
    history.find((entry) => new Date(entry.createdAt).getTime() <= recordedAtMs) ?? history[history.length - 1];

  return atTime ?? history[0]!;
}

export function setupMatches(left: KartSetupSummary, right: KartSetupSummary) {
  return (
    left.frontTrackWidthMm === right.frontTrackWidthMm &&
    left.rearTrackWidthMm === right.rearTrackWidthMm &&
    left.seatPosition === right.seatPosition &&
    left.gearRatioDriver === right.gearRatioDriver &&
    left.gearRatioAxle === right.gearRatioAxle &&
    left.frontTirePressurePsi === right.frontTirePressurePsi &&
    left.rearTirePressurePsi === right.rearTirePressurePsi &&
    left.totalWeightKg === right.totalWeightKg
  );
}
